import { apiService } from './api';

export interface MissingStock {
  symbol: string;
  exchange: 'NS' | 'BO';
  status: 'delisted' | 'symbol_change' | 'active';
  corrected_symbol?: string;
  notes?: string;
}

export interface MissingStocksResponse {
  stocks: MissingStock[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export interface AdminStats {
  stocks: {
    total_in_db: number;
    price_files: number;
    analysis_records: number;
    missing_stocks: number;
    gap_analysis_vs_files: number;
  };
  corrections: {
    total: number;
    unresolved: number;
    resolved: number;
  };
  system: {
    last_updated: string;
    data_coverage: number;
  };
}

class AdminService {
  async getMissingStocks(
    page: number = 1, 
    perPage: number = 25, 
    search: string = '', 
    status: string = 'all'
  ): Promise<MissingStocksResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      search,
      status
    });

    console.log('🔍 AdminService: Making API call to:', `/admin/missing-stocks?${params}`);
    const response = await apiService.get(`/admin/missing-stocks?${params}`);
    console.log('🔍 AdminService: Raw API response:', response);
    
    // Handle case where response might be the data directly
    if (response && typeof response === 'object' && 'stocks' in response) {
      return response as MissingStocksResponse;
    }
    
    // Handle case where response.data exists
    if (response && typeof response === 'object' && 'data' in response) {
      return (response as any).data;
    }
    
    // Fallback - return empty response
    console.warn('⚠️ AdminService: Unexpected response format, returning empty data');
    return {
      stocks: [],
      pagination: {
        page: page,
        per_page: perPage,
        total: 0,
        pages: 0
      }
    };
  }

  async updateMissingStock(symbol: string, data: Partial<MissingStock>): Promise<{ message: string }> {
    const response = await apiService.put(`/admin/missing-stocks/${encodeURIComponent(symbol)}`, data);
    return response as { message: string };
  }

  async deleteMissingStock(symbol: string): Promise<{ message: string }> {
    const response = await apiService.delete(`/admin/missing-stocks/${encodeURIComponent(symbol)}`);
    return response as { message: string };
  }

  async addMissingStock(data: MissingStock): Promise<{ message: string }> {
    const response = await apiService.post('/admin/missing-stocks', data);
    return response as { message: string };
  }

  async getAdminStats(): Promise<AdminStats> {
    const response = await apiService.get('/admin/stats');
    return response as AdminStats;
  }

  async refreshAnalysis(): Promise<{ message: string }> {
    const response = await apiService.post('/admin/refresh-analysis');
    return response as { message: string };
  }
}

export const adminService = new AdminService();

